import React from 'react'
import Link from 'next/link'

import { DirectoryLinkHref, DirectoryLinkAs, WithPage } from 'utils/DirectoryParser'
import {
  PaginationContainer,
  PaginationLeftButton,
  PaginationMiddleButton,
  PaginationEllipsisButton,
  PaginationRightButton,
  PaginationAnchor,
} from './Styled'

const PAGINATION_MAX_BREADTH = 2
const PAGINATION_START = 2

const getBackground = (isSelected, isSelectable) => {
  if (isSelected && !isSelectable) {
    return 'inverseColor'
  } else if (!isSelected && !isSelectable) {
    return null
  }

  return 'grey'
}

const getInnerContent = (isSelectable, value, page, directoryLinkInformation) => {
  if (!isSelectable) {
    return value
  }
  const copy = WithPage(directoryLinkInformation, page)
  const directoryLinkHref = DirectoryLinkHref(copy)
  const directoryAs = DirectoryLinkAs(copy)
  return (
    <Link href={directoryLinkHref} as={directoryAs}>
      <PaginationAnchor>{value}</PaginationAnchor>
    </Link>
  )
}

const basePaginationButton = (value, isBeginning = false, isEnd = false, linkInformation = {}) => {
  const { isSelected, isSelectable, page, directoryLinkInformation } = linkInformation
  const innerContent = getInnerContent(isSelectable, value, page, directoryLinkInformation)
  const background = getBackground(isSelected, isSelectable)

  if (isBeginning) {
    return (
      <PaginationLeftButton key={value} bg={background}>
        {isSelectable ? <>{innerContent}</> : <PaginationAnchor>{innerContent}</PaginationAnchor>}
      </PaginationLeftButton>
    )
  } else if (isEnd) {
    return (
      <PaginationRightButton key={value} bg={background}>
        {isSelectable ? <>{innerContent}</> : <PaginationAnchor>{innerContent}</PaginationAnchor>}
      </PaginationRightButton>
    )
  }
  return (
    <PaginationMiddleButton key={value} bg={background}>
      {isSelectable ? <>{innerContent}</> : <PaginationAnchor>{innerContent}</PaginationAnchor>}
    </PaginationMiddleButton>
  )
}

const paginationButton = (
  index,
  page,
  isBeginning = false,
  isEnd = false,
  directoryLinkInformation = null
) => {
  const isSelected = page === index
  return basePaginationButton(index, isBeginning, isEnd, {
    isSelectable: !isSelected,
    isSelected: isSelected,
    page: index,
    directoryLinkInformation: directoryLinkInformation,
  })
}

/**
 * This builds the pagination options
 *
 * Some examples
 *
 * [1] 2 3 4 5 ... >>
 * << 1 2 [3] 4 5 ... >>
 * << 1 ... 4 5 [6] 7 8 ... >>
 *
 * @param page current page selected
 * @param count total items available
 * @param perPage items per page
 * @returns {null|*}
 * @constructor
 */
const Paginator = ({ page = 1, count = 0, perPage = 10, directoryLinkInformation }) => {
  if (page < 1 || count < 1 || perPage < 1 || count <= perPage) {
    return null
  }
  const paginationButtons = []
  const paginationStart = Math.max(PAGINATION_START, page - PAGINATION_MAX_BREADTH)
  const paginationLimit = Math.ceil(count / perPage)
  const paginationEnd = page + Math.min(paginationLimit - page, PAGINATION_MAX_BREADTH)
  const isAtEnd = paginationLimit === paginationEnd
  const isAtBeginning = paginationStart === PAGINATION_START
  const hasLeadingEllipses = paginationStart > PAGINATION_START
  const hasTrailingEllipses = paginationEnd < paginationLimit

  if (!isAtBeginning) {
    paginationButtons.push(
      basePaginationButton(String.fromCharCode(171), true, false, {
        isSelectable: true,
        isSelected: false,
        page: page - 1,
        directoryLinkInformation: directoryLinkInformation,
      })
    )
  }

  paginationButtons.push(paginationButton(1, page, isAtBeginning, false, directoryLinkInformation))

  if (hasLeadingEllipses) {
    paginationButtons.push(
      <PaginationEllipsisButton key='leadingEllipsis' bg='grey'>
        &#8230;
      </PaginationEllipsisButton>
    )
  }

  for (let index = paginationStart; index <= paginationEnd; index++) {
    paginationButtons.push(
      paginationButton(index, page, false, index === paginationLimit, directoryLinkInformation)
    )
  }

  if (hasTrailingEllipses) {
    paginationButtons.push(
      <PaginationEllipsisButton key='trailingEllipsis' bg='grey'>
        &#8230;
      </PaginationEllipsisButton>
    )
  }

  if (!isAtEnd) {
    paginationButtons.push(
      basePaginationButton(String.fromCharCode(187), false, true, {
        isSelectable: true,
        isSelected: false,
        page: page + 1,
        directoryLinkInformation: directoryLinkInformation,
      })
    )
  }

  return <PaginationContainer>{paginationButtons}</PaginationContainer>
}

export default Paginator
